<template>
  <div>
    <MapLayout>
      <template v-slot:map-icon>
        <MapIcon class="map-icon" />
        <span>view on map</span>
      </template>
      <template v-slot:content>
        <div class="content">
          <header>
            <div class="address-bar">
              <span>
                <span v-if="address" class="address">{{ address }}</span>
                <RouterLink :to="resetSearchRoute" class="reset-link"
                  >Search again</RouterLink
                >
              </span>
              <RouterLink to="/" class="reset-btn">&times;</RouterLink>
            </div>
            <HeaderText tag="h1">{{ headline }}</HeaderText>
            <p v-if="description" class="description">{{ description }}</p>
            <BaseButton
              v-if="showUpdateButton"
              tag="router-link"
              :to="resetSearchRoute"
              class="update-btn"
            >
              Update status
            </BaseButton>
          </header>
          <div class="results-container">
            <DonutLoader v-if="isLoading" class="spinner" />
            <div v-if="error" class="error-message">{{ error }}</div>
            <button
              v-for="(item, i) in results"
              :key="item.id"
              @click="onHospitalClick(item)"
              type="button"
              class="result-item"
            >
              <MapPin class="result-pin result-icon" />
              <div class="result-details">
                <div class="hospital-name">{{ i + 1 }}. {{ item.name }}</div>
                <address class="hospital-address">
                  {{ getAddress(item.location) }}
                </address>
                <div class="hospital-stats">
                  <div v-if="item.travel_information" class="driving-time">
                    <CarIcon class="car-icon" />{{
                      getTravelTime(item.travel_information)
                    }}
                  </div>
                  <div
                    v-for="resource in getAvailableResources(item)"
                    :key="resource.key"
                    class="hospital-stat"
                  >
                    <div
                      class="resource-count"
                      :class="{ selected: resources.includes(resource.key) }"
                    >
                      {{ resource.value }}
                    </div>
                    <div class="resource-label">{{ resource.label }}</div>
                  </div>
                </div>
              </div>
              <ArrowRight class="result-arrow result-icon" />
            </button>
          </div>
        </div>
      </template>
      <template v-slot:map>
        <ResultsMap
          class="map"
          :dataset="results"
          :initialCenter="{ lat, lng }"
          :focusedMarker="focusedMarker"
          @mapLoaded="onMapLoaded"
          @hospitalClick="onHospitalClick"
        />
      </template>
    </MapLayout>
    <MobileHospitalOverlay
      v-if="$mq === 'sm'"
      :class="{ active: selectedHospital }"
      :hospitalData="selectedHospital"
      :userAddress="address"
      @close="selectedHospital = null"
    />
  </div>
</template>

<script>
import Api from '@/store/api';
import ArrowRight from '@/assets/arrow-right.svg';
import BaseButton from '@/components/base-button';
import CarIcon from '@/assets/car-icon.svg';
import DonutLoader from '@/components/static-donut-chart.vue';
import HeaderText from '@/components/header-text';
import MapIcon from '@/assets/map-icon.svg';
import MapLayout from '@/components/map-layout';
import MobileHospitalOverlay from '@/components/ResultsMap/mobile-hospital-overlay';
import MapPin from '@/assets/map-pin.svg';
import ResultsMap from '@/components/ResultsMap';
import get from 'lodash/get';
import getAddress from '@/util/getAddress';
import { CareNeeds, PatientIntent } from '@/store/types';
import { RouterAliases } from '@/router';
import { StoreGetters, collectUserDetails } from '@/store';
import { TargetAudiences } from '@/store/types';
import { mapGetters } from 'vuex';

export default {
  components: {
    ArrowRight,
    BaseButton,
    CarIcon,
    DonutLoader,
    HeaderText,
    MapIcon,
    MapLayout,
    MapPin,
    MobileHospitalOverlay,
    ResultsMap,
  },
  props: {
    address: {
      type: String,
      default: null,
    },
    age: {
      type: String,
      default: null,
    },
    audience: {
      type: String,
      default: TargetAudiences.DEFAULT,
    },
    condition: {
      type: Number,
      default: null,
    },
    gender: {
      type: String,
      default: null,
    },
    lat: {
      type: Number,
      required: true,
    },
    lng: {
      type: Number,
      required: true,
    },
    resources: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      focusedMarker: null,
      error: null,
      isLoading: true,
      results: [],
      selectedHospital: null,
    };
  },
  computed: {
    ...mapGetters({
      name: StoreGetters.GET_USER_NAME,
    }),
    conditionString() {
      if (this.condition === null) {
        return null;
      }
      const strings = [
        'no symptoms',
        'mild symptoms',
        'moderate symptoms',
        'critical symptoms',
      ];
      return strings[this.condition];
    },
    description() {
      switch (this.audience) {
        case TargetAudiences.DOCTOR:
        case TargetAudiences.EMT:
          return null;
        default:
          return 'Plan for future hospital care needs by considering these matches below.';
      }
    },
    headline() {
      let result = '';

      const getDefaultHeadline = () => {
        let headline = 'Top Matches';
        if (this.resources.length) {
          headline += ` for ${this.resources
            .map(key => CareNeeds[key])
            .join(', ')}`;
        }
        return headline;
      };

      switch (this.audience) {
        case TargetAudiences.DOCTOR:
        case TargetAudiences.EMT:
          result = getDefaultHeadline();
          break;
        default:
          if (this.conditionString) {
            result += this.name ? `${this.name}, you’re` : 'You’re';
            result += ` experiencing ${this.conditionString}`;
          } else {
            result = this.name
              ? `Top matches for ${this.name}`
              : getDefaultHeadline();
          }
          break;
      }
      return result;
    },
    resetSearchRoute() {
      let name;
      switch (this.audience) {
        case TargetAudiences.DOCTOR:
        case TargetAudiences.EMT:
          name = RouterAliases.DOCTOR_ASSESSMENT;
          break;
        default:
          name = RouterAliases.PATIENT_ASSESSMENT;
          break;
      }
      return {
        name,
        params: {
          step: 1,
        },
      };
    },
    showUpdateButton() {
      return (
        this.audience !== TargetAudiences.DOCTOR &&
        this.audience !== TargetAudiences.EMT
      );
    },
  },
  created() {
    this.map = null;
    this.fetchResults();
  },
  methods: {
    centerMap(item) {
      // When clicking on a search result list item, this moves the map to the
      // selected item and then opens the popup for that item.
      //
      // TODO(jjandoc): There is a minor bug where the new popup may not open
      //   if it would be positioned closely to a currently open popup and that
      //   previous popup was opened by clicking on a map pin as opposed to the
      //   list view.
      if (!this.map) return;
      const { lat, lng } = item.location;
      this.map.easeTo({ center: [lng, lat] });
      this.focusedMarker = item.id;
    },
    async fetchResults() {
      this.error = null;
      try {
        const initialBody = await collectUserDetails(
          {
            state: this.condition || 0,
            covidState: 0,
            location: {
              lat: this.lat,
              lng: this.lng,
            },
            needs: {},
            careNeeds: [],
          },
          PatientIntent.NEEDHELP,
        );
        const requestBody = {
          ...initialBody,
          immediate_medical_needs: this.resources,
          patient_demographics: [this.age, this.gender].filter(
            item => item !== null,
          ),
        };
        this.results = await Api.loadMatches(requestBody);
      } catch (err) {
        this.error = err.message;
      } finally {
        this.isLoading = false;
      }
    },
    getAddress,
    getAvailableResources(hospital) {
      return [
        {
          key: 'ICU',
          label: CareNeeds.ICU,
          value: get(hospital, 'status.icu_beds.available.value', null),
        },
        {
          key: 'VENTILATOR',
          label: CareNeeds.VENTILATOR,
          value: get(
            hospital,
            'status.adult_ventilators.available.value',
            null,
          ),
        },
      ].filter(({ value }) => value !== null);
    },
    getTravelTime(travel_information = {}) {
      const { travel_time } = travel_information;
      return travel_time ? `${Math.floor(travel_time / 60)} min` : null;
    },
    onHospitalClick(item) {
      this.selectedHospital = item;
      if (this.$mq !== 'sm') {
        this.centerMap(item);
      }
    },
    onMapLoaded(map) {
      this.map = map;
    },
  },
};
</script>

<style lang="scss" scoped>
.map-icon {
  margin-bottom: 8px;
}

.address-bar {
  align-items: top;
  border-top: 1px solid var(--white);
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-top: 12px;
  font-weight: 500;
}

.address {
  margin-right: 0.5em;
  max-width: 150px;
  overflow: hidden;
  display: inline-block;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.reset-link {
  color: var(--yellow);
  font-weight: bold;
  text-decoration: none;
  vertical-align: middle;
}

.reset-btn {
  color: var(--white);
  font-size: 18px;
  font-weight: bold;
  line-height: 12px;
  margin-left: 16px;
  text-decoration: none;
}

.description {
  color: var(--white-transparent);
  margin-top: 24px;
}

.update-btn {
  margin-top: 32px;
}

.results-container {
  display: flex;
  flex-direction: column;
  margin-top: 32px;
}

.spinner {
  font-size: 3em;
  margin: auto;
}

.result-item {
  align-items: baseline;
  border-top: 1px solid var(--white-transparent-lighter);
  display: flex;
  padding: 16px 0;
  width: 100%;
}

.result-icon {
  flex-shrink: 0;
}

.result-pin {
  color: var(--yellow);
  margin-right: 12px;
}

.result-details {
  flex: 1;
  text-align: left;
}

.hospital-name {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2em;
}

.hospital-address {
  color: var(--white-transparent);
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
}

.hospital-stats {
  display: flex;
  font-size: 12px;
  margin: 0 -8px;
}

.driving-time,
.resource-count {
  border: 1px solid var(--dark-blue);
  border-radius: 100px;
  color: var(--dark-blue);
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  height: 26px;
  margin: 8px 8px 0;
  padding: 3px 12px 2px;
  text-align: center;
  white-space: nowrap;
}

.resource-count {
  font-weight: bold;
  padding: 3px 4px 2px;
  min-width: 26px;

  &.selected {
    background: var(--dark-blue);
    color: var(--yellow);
  }
}

.car-icon {
  margin-right: 8px;
}

.hospital-stat {
  align-items: baseline;
  color: var(--dark-blue);
  display: flex;
  white-space: nowrap;
}

.result-arrow {
  margin-left: 12px;
}

.map {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
}

.mobile-hospital-overlay {
  transform: translateX(100%);
  transition: transform 0.3s ease;

  &.active {
    transform: translateX(0);
  }
}
</style>
